import React, { useState, useEffect } from 'react';
import { Truck, User, Phone, Mail, MapPin, FileText } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import { isValidEmail } from '../../utils/validators';
import './ProveedorForm.css';

const ProveedorForm = ({ proveedor, onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        contacto: '',
        telefono: '',
        email: '',
        direccion: '',
        rfc: '',
        activo: 1
    });

    const [errors, setErrors] = useState({});

    // Cargar datos del proveedor si estamos editando
    useEffect(() => {
        if (isEdit && proveedor) {
            setFormData({
                nombre: proveedor.nombre || '',
                contacto: proveedor.contacto || '',
                telefono: proveedor.telefono || '',
                email: proveedor.email || '',
                direccion: proveedor.direccion || '',
                rfc: proveedor.rfc || '',
                activo: proveedor.activo ?? 1
            });
        }
    }, [isEdit, proveedor]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar nombre (requerido)
        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre del proveedor es requerido';
        } else if (formData.nombre.length < 2) {
            newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
        }

        // Validar teléfono (requerido)
        if (!formData.telefono.trim()) {
            newErrors.telefono = 'El teléfono es requerido';
        } else if (!/^\d{10}$/.test(formData.telefono.replace(/\D/g, ''))) {
            newErrors.telefono = 'El teléfono debe tener 10 dígitos';
        }

        // Validar email (opcional pero con formato si se proporciona)
        if (formData.email && !isValidEmail(formData.email)) {
            newErrors.email = 'El email no es válido';
        }

        // Validar RFC (opcional pero con formato si se proporciona)
        if (formData.rfc && !/^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/i.test(formData.rfc)) {
            newErrors.rfc = 'El RFC no tiene un formato válido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Preparar datos para enviar
        const dataToSubmit = {
            nombre: formData.nombre.trim(),
            contacto: formData.contacto.trim(),
            telefono: formData.telefono.replace(/\D/g, ''),
            email: formData.email.trim().toLowerCase(),
            direccion: formData.direccion.trim(),
            rfc: formData.rfc.trim().toUpperCase(),
            activo: parseInt(formData.activo)
        };

        onSubmit(dataToSubmit);
    };

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) {
            return numbers;
        } else if (numbers.length <= 6) {
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        } else {
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        handleInputChange('telefono', formatted);
    };

    return (
        <form onSubmit={handleSubmit} className="proveedor-form">
            <div className="form-container">
                {/* Columna Izquierda - Información Básica */}
                <div className="form-column">
                    <h3 className="column-title">
                        <Truck size={18} />
                        Información del Proveedor
                    </h3>

                    {/* Nombre */}
                    <div className="form-group">
                        <Input
                            label="Nombre del Proveedor"
                            type="text"
                            value={formData.nombre}
                            onChange={(e) => handleInputChange('nombre', e.target.value)}
                            error={errors.nombre}
                            icon={<Truck size={18} />}
                            placeholder="Ej: Distribuidora del Norte"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Contacto */}
                    <div className="form-group">
                        <Input
                            label="Persona de Contacto"
                            type="text"
                            value={formData.contacto}
                            onChange={(e) => handleInputChange('contacto', e.target.value)}
                            error={errors.contacto}
                            icon={<User size={18} />}
                            placeholder="Ej: Juan Pérez"
                            disabled={isLoading}
                        />
                        <small className="form-help">Opcional</small>
                    </div>

                    {/* Teléfono */}
                    <div className="form-group">
                        <Input
                            label="Teléfono"
                            type="tel"
                            value={formData.telefono}
                            onChange={handlePhoneChange}
                            error={errors.telefono}
                            icon={<Phone size={18} />}
                            placeholder="555-123-4567"
                            required
                            disabled={isLoading}
                            maxLength={12}
                        />
                    </div>

                    {/* Email */}
                    <div className="form-group">
                        <Input
                            label="Correo Electrónico"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={errors.email}
                            icon={<Mail size={18} />}
                            placeholder="contacto@proveedor.com"
                            disabled={isLoading}
                        />
                        <small className="form-help">Opcional</small>
                    </div>
                </div>

                {/* Columna Derecha - Información Adicional */}
                <div className="form-column">
                    <h3 className="column-title">
                        <FileText size={18} />
                        Información Adicional
                    </h3>

                    {/* RFC */}
                    <div className="form-group">
                        <Input
                            label="RFC"
                            type="text"
                            value={formData.rfc}
                            onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                            error={errors.rfc}
                            icon={<FileText size={18} />}
                            placeholder="XAXX010101000"
                            disabled={isLoading}
                            maxLength={13}
                        />
                        <small className="form-help">Opcional - Registro Federal de Contribuyentes</small>
                    </div>

                    {/* Dirección */}
                    <div className="form-group">
                        <label className="form-label">
                            <MapPin size={16} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                            Dirección
                        </label>
                        <textarea
                            value={formData.direccion}
                            onChange={(e) => handleInputChange('direccion', e.target.value)}
                            className={`form-textarea ${errors.direccion ? 'error' : ''}`}
                            placeholder="Calle, número, colonia, ciudad, estado, CP"
                            disabled={isLoading}
                            rows={3}
                        />
                        {errors.direccion && (
                            <span className="error-message">{errors.direccion}</span>
                        )}
                        <small className="form-help">Opcional</small>
                    </div>

                    {/* Estado (solo para edición) */}
                    {isEdit && (
                        <div className="form-group">
                            <label className="form-label">Estado del Proveedor</label>
                            <select
                                value={formData.activo}
                                onChange={(e) => handleInputChange('activo', e.target.value)}
                                className="form-select"
                                disabled={isLoading}
                            >
                                <option value={1}>Activo</option>
                                <option value={0}>Inactivo</option>
                            </select>
                            <small className="form-help">
                                Los proveedores inactivos no aparecen en las órdenes de compra
                            </small>
                        </div>
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>

                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                >
                    {isEdit ? 'Actualizar Proveedor' : 'Crear Proveedor'}
                </Button>
            </div>
        </form>
    );
};

export default ProveedorForm;
